import { useMemo, useState } from 'react';
import { useLocale } from '@platform/core';
import { useBudget } from '../contexts/BudgetContext';
import { ProjectFilters } from '../components/projects/ProjectFilters';
import { ProjectsTable } from '../components/projects/ProjectsTable';
import { ProjectStatistics } from '../components/projects/ProjectStatistics';
import { ProjectFormModal } from '../components/projects/ProjectFormModal';

export const ProjectsPage: React.FC = () => {
  const { t } = useLocale();
  const {
    filteredProjects,
    projectFilters,
    setProjectFilters,
    projects,
    currentBudget,
    deleteProject,
    isLoading,
  } = useBudget();

  const [isFormOpen, setIsFormOpen] = useState(false);

  // Extract unique filter values from all projects
  const filterOptions = useMemo(() => {
    const projectManagers = new Set<string>();
    const managers = new Set<string>();
    const clients = new Set<string>();
    const contracts = new Set<string>();

    projects.forEach(p => {
      if (p.projectManager) projectManagers.add(p.projectManager);
      if (p.manager) managers.add(p.manager);
      if (p.client) clients.add(p.client);
      if (p.contractNumber) contracts.add(p.contractNumber);
    });

    return {
      projectManagers: Array.from(projectManagers).sort(),
      managers: Array.from(managers).sort(),
      clients: Array.from(clients).sort(),
      contracts: Array.from(contracts).sort(),
    };
  }, [projects]);

  const handleDeleteProject = async (projectId: string) => {
    if (confirm(t('common.confirmDelete'))) {
      await deleteProject(projectId);
    }
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>{t('general.loading')}</p>
      </div>
    );
  }

  return (
    <div className="projects-page">
      <div className="page-header">
        <h1>{t('budget.projects.title')}</h1>
        <div className="header-info">
          <span className="project-count">
            {filteredProjects.length} / {projects.length} {t('budget.projects.projects')}
          </span>
          <button className="btn btn-primary" onClick={() => setIsFormOpen(true)}>
            + {t('budget.projects.createProject')}
          </button>
        </div>
      </div>

      <ProjectFilters
        filters={projectFilters}
        onChange={setProjectFilters}
        projectManagers={filterOptions.projectManagers}
        managers={filterOptions.managers}
        clients={filterOptions.clients}
        contracts={filterOptions.contracts}
      />

      {filteredProjects.length === 0 ? (
        <div className="empty-state">
          {projects.length === 0 ? (
            <>
              <div className="empty-icon">📋</div>
              <h3>{t('budget.projects.noProjects')}</h3>
              <p>{t('budget.projects.noProjectsDescription')}</p>
            </>
          ) : (
            <>
              <div className="empty-icon">🔍</div>
              <h3>{t('budget.projects.noMatchingProjects')}</h3>
              <p>{t('budget.projects.adjustFilters')}</p>
              <button
                className="btn btn-secondary"
                onClick={() => setProjectFilters({
                  search: '',
                  status: 'all',
                  projectManager: '',
                  manager: '',
                  client: '',
                  contract: '',
                  dateFrom: '',
                  dateTo: '',
                })}
              >
                {t('common.clearFilters')}
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          <ProjectsTable
            projects={filteredProjects}
            currency={currentBudget?.currency}
            onDeleteProject={handleDeleteProject}
          />
          <ProjectStatistics />
        </>
      )}

      <ProjectFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
      />

      <style>{`
        .projects-page {
          max-width: 1600px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-header h1 {
          margin: 0;
          font-size: 1.5rem;
        }

        .header-info {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .project-count {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow);
        }

        .empty-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .empty-state h3 {
          margin: 0 0 0.5rem;
          font-size: 1.25rem;
        }

        .empty-state p {
          color: var(--color-text-secondary);
          margin: 0 0 1.5rem;
        }

        .btn {
          padding: 0.5rem 1rem;
          border-radius: var(--radius);
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary {
          background: var(--color-primary);
          color: white;
          border: none;
        }

        .btn-primary:hover {
          opacity: 0.9;
        }

        .btn-secondary {
          background: var(--color-background);
          color: var(--color-text);
          border: 1px solid var(--color-border);
        }

        .btn-secondary:hover {
          background: var(--color-border);
        }

        .loading-screen {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          gap: 1rem;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--color-border);
          border-top-color: var(--color-primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};
